import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] },
});

const experiences = [
  {
    company: 'Zoolarity Labs',
    role: 'Full Stack Developer Intern',
    duration: 'May 2026 – Jul 2026',
    status: 'completed',
    description: 'Contributed to the ESF-Bravo application, building and optimising secure backend systems with measurable performance gains.',
    achievements: [
      'Built secure REST APIs using NestJS, PostgreSQL, and TypeORM — implementing rate limiting, access controls, and FK column indexing',
      'Optimised backend APIs from 15s → 400ms response time; enhanced search algorithms for faster, more accurate results',
    ],
    tech: ['NestJS', 'PostgreSQL', 'TypeORM', 'React.js', 'TanStack'],
    initials: 'ZL',
    color: '#7c3aed',
  },
  {
    company: 'NexG.ai — SavingsYogi',
    role: 'Full Stack · Cloud · DevOps Intern',
    duration: 'Jan 2025 – Mar 2025',
    status: 'completed',
    description: 'Built scalable web applications focused on financial savings solutions — handled full deployment lifecycle on AWS.',
    achievements: [],
    tech: ['JavaScript', 'Express.js', 'MongoDB', 'Redis', 'React', 'AWS', 'Docker'],
    initials: 'NG',
    color: '#0891b2',
  },
];

function ExperienceRow({ exp, delay }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div {...fadeUp(delay)} className="border-b border-black/8 last:border-0">
      {/* Row */}
      <button
        onClick={() => setOpen(v => !v)}
        className="group w-full flex items-center gap-4 py-4 hover:bg-black/[0.015] transition-colors px-1 -mx-1 rounded-sm text-left"
      >
        {/* Avatar */}
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[11px] font-black shrink-0 shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]"
          style={{ background: exp.color }}
        >
          {exp.initials}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[14px] font-black text-black">{exp.company}</span>
          </div>
          <p className="text-[11px] text-black/45 font-mono truncate">{exp.role}</p>
        </div>

        {/* Duration */}
        <span className="hidden sm:block text-[10px] font-mono text-black/35 shrink-0">{exp.duration}</span>

        {/* Chevron */}
        <ChevronDown
          size={14}
          className={`shrink-0 text-black/25 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expandable detail */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-5 pl-[52px] pr-1">
              {/* Mobile duration */}
              <p className="sm:hidden text-[10px] font-mono text-black/35 mb-3">{exp.duration}</p>

              <p className="text-[12px] text-gray-600 leading-relaxed mb-3">{exp.description}</p>

              {exp.achievements.length > 0 && (
                <ul className="space-y-2 mb-3">
                  {exp.achievements.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-gray-600 leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap gap-1.5">
                {exp.tech.map(t => (
                  <span
                    key={t}
                    className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-wide text-black/45 bg-black/4 rounded-md border border-black/8"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Experience() {
  return (
    <section id="experience" className="relative w-full overflow-hidden bg-transparent py-8 lg:py-10">
      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 lg:px-14">

        {/* Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <motion.p {...fadeUp(0)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-1">
              &lt;/Experience&gt;
            </motion.p>
            <motion.h1 {...fadeUp(0.04)} className="text-2xl sm:text-3xl font-black text-black">
              Work History
            </motion.h1>
          </div>
          <motion.div {...fadeUp(0.08)} className="pb-0.5 text-right">
            <span className="text-lg font-black text-black leading-none block">{experiences.length}</span>
            <span className="text-[9px] font-mono text-black/30 uppercase tracking-wider mt-0.5 block">roles</span>
          </motion.div>
        </div>

        <motion.div {...fadeUp(0.1)} className="h-px bg-black/10 mb-0" />

        {/* Rows */}
        <div>
          {experiences.map((exp, i) => (
            <ExperienceRow key={exp.company} exp={exp} delay={0.1 + i * 0.06} />
          ))}
        </div>

      </div>
    </section>
  );
}